import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { motion } from "motion/react";
import { useState, useRef } from "react";
import { DateTime } from "luxon";
import { cn } from "../utils/cn.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { RichTextWithImageZoom } from "./RichTextWithImageZoom.js";
import { DrawerOrDialog } from "./DrawerOrDialog.js";
import { NoteCard, type NoteData } from "./NoteCard.js";

type NotesSidebarProps<T extends NoteData> = {
  notes: T[];
  layoutId?: string;
};

const extractTitle = (note: NoteData): string => {
  if (note.name) return note.name;
  if (note.description) return note.description;

  const h1Match = note.content.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
  if (h1Match) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(h1Match[1], "text/html");
    return doc.body.textContent?.trim() || "Untitled";
  }

  const pMatch = note.content.match(/<p[^>]*>(.*?)<\/p>/i);
  if (pMatch) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(pMatch[1], "text/html");
    const text = doc.body.textContent?.trim();
    if (text) return text.length > 40 ? text.slice(0, 40) + "..." : text;
  }

  return "Untitled";
};

const NoteDrawer = ({ note }: { note: NoteData }) => {
  const isGummy = useIsGummy();
  return (
    <DrawerOrDialog.Root drawer={{ modal: true }} dialog={{}}>
      <DrawerOrDialog.Trigger asChild>
        <div>
          <NoteCard note={note} className="cursor-pointer" />
        </div>
      </DrawerOrDialog.Trigger>
      <DrawerOrDialog.Content className="md:max-w-(--breakpoint-md)">
        <div className="flex flex-1 flex-col overflow-hidden">
          <DrawerOrDialog.Title
            className={cn(
              "px-4 pb-4 pt-4 md:px-0 md:pt-0",
              !isGummy && "border-border border-b"
            )}
          >
            <div className="flex items-center gap-x-3">
              <div
                className={cn(
                  isGummy
                    ? cn(
                        "shadow-sunken p-2.5",
                        "rounded-surface-sm bg-white dark:bg-mono-900 text-rose-500 dark:text-rose-300"
                      )
                    : "p-1.5 rounded-md bg-amber-50 text-amber-500 dark:bg-amber-500/10"
                )}
              >
                <DocumentTextIcon
                  className={cn(
                    "flex-none",
                    isGummy ? "size-8" : "size-7 mt-0.5"
                  )}
                  strokeWidth={isGummy ? 2.5 : undefined}
                />
              </div>
              <h4
                className={cn(
                  "flex-1 text-left leading-tight dark:text-white",
                  isGummy
                    ? "text-2xl font-black tracking-tight text-mono-900"
                    : "text-lg font-normal"
                )}
              >
                Note
              </h4>
            </div>
          </DrawerOrDialog.Title>
          <div className="mt-4 flex min-h-0 flex-1 overflow-auto px-4 pb-6 md:px-0 md:pb-2">
            <RichTextWithImageZoom html={note?.content ?? ""} />
          </div>
        </div>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

const NotesMobile = <T extends NoteData>({ notes }: NotesSidebarProps<T>) => (
  <div className="w-full space-y-3 pt-4">
    {notes.map((note) => (
      <NoteDrawer note={note} key={note.id} />
    ))}
  </div>
);

const NotesDesktop = <T extends NoteData>({
  notes,
  layoutId = "notes"
}: NotesSidebarProps<T>) => {
  const isGummy = useIsGummy();
  const [selectedNote, setSelectedNote] = useState(notes[0]);
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToTop = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="flex min-h-[50vh] w-full flex-1 gap-x-6">
      <aside
        className={cn(
          "flex-none overflow-y-auto pr-2",
          isGummy ? "w-72" : "w-56",
          !isGummy && "dark:border-mono-700/50 border-r"
        )}
      >
        <div className={cn("py-3", isGummy ? "space-y-2" : "space-y-1")}>
          {notes.map((note) => {
            const title = extractTitle(note);
            const date = DateTime.fromISO(note.created_at);
            const isSelected = note.id === selectedNote.id;

            return (
              <button
                key={note.id}
                onClick={() => {
                  setSelectedNote(note);
                  scrollToTop();
                }}
                className={cn(
                  "relative flex w-full cursor-pointer items-center gap-3 text-left transition-all",
                  isGummy
                    ? "min-h-16 rounded-surface-sm px-4 py-3 hover:bg-white/60 dark:hover:bg-mono-900/60"
                    : "rounded-lg px-3 py-3 hover:bg-mono-50 dark:hover:bg-mono-800/50"
                )}
              >
                {isSelected && (
                  <motion.div
                    layoutId={`${layoutId}Bg`}
                    className={cn(
                      "absolute",
                      isGummy
                        ? "inset-1 rounded-surface-xs bg-gradient-to-b from-pink-400/20 to-rose-600/20 dark:from-pink-400/25 dark:to-rose-600/25"
                        : "inset-y-1 left-0 w-0.5 rounded-l-full bg-amber-500"
                    )}
                    transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                  />
                )}
                <div
                  className={cn(
                    "relative shrink-0 flex items-center justify-center text-white",
                    isGummy
                      ? cn(
                          "h-12 w-12 rounded-surface-xs bg-tile-schedule",
                          "shadow-raised text-shadow-on-color-strong"
                        )
                      : "h-8 w-8 rounded-md bg-amber-50 text-amber-500 dark:bg-amber-950 dark:text-amber-400"
                  )}
                >
                  <DocumentTextIcon
                    className={cn(isGummy ? "size-6" : "size-4")}
                    strokeWidth={isGummy ? 2.5 : undefined}
                  />
                </div>
                <div className="relative min-w-0 flex-1">
                  <h5
                    className={cn(
                      "truncate text-mono-900 dark:text-white",
                      isGummy
                        ? "text-base font-black tracking-tight"
                        : "text-sm font-medium"
                    )}
                  >
                    {title}
                  </h5>
                  <p
                    className={cn(
                      isGummy
                        ? "text-mono-600 dark:text-mono-300"
                        : "text-mono-500 dark:text-mono-400",
                      isGummy
                        ? "mt-1 text-xs font-black uppercase tracking-widest"
                        : "text-xs"
                    )}
                  >
                    {date.toFormat("MMM dd, yyyy")}
                  </p>
                </div>
                {isSelected && (
                  <motion.div
                    layoutId={`${layoutId}Chevron`}
                    transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                    className="relative"
                  >
                    <ChevronRightIcon
                      className={cn(
                        isGummy
                          ? "size-7 text-rose-500 dark:text-rose-400"
                          : "size-5 text-mono-400"
                      )}
                    />
                  </motion.div>
                )}
              </button>
            );
          })}
        </div>
      </aside>
      <section ref={sectionRef} className="flex-1 overflow-y-auto">
        <div className="py-4">
          <RichTextWithImageZoom html={selectedNote?.content ?? ""} />
        </div>
      </section>
    </div>
  );
};

const NotesSidebar = <T extends NoteData>({
  notes,
  layoutId = "notes"
}: NotesSidebarProps<T>) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (isDesktop) {
    return <NotesDesktop notes={notes} layoutId={layoutId} />;
  }

  return <NotesMobile notes={notes} />;
};

export { NotesSidebar, NotesDesktop, NotesMobile, NoteDrawer, extractTitle };
export type { NotesSidebarProps };
